import React, { useState } from "react";
import {
  BookOpen, Calculator, CircleUser, Download, FileText, Gauge, Home, LogOut, MessageSquare, MoreHorizontal,
  Newspaper, Scale, Search, Settings as SettingsIcon, StickyNote, X,
} from "lucide-react";
import { MENU_ITEMS, resolveVisibleMenus } from "../config/menuConfig";
import { isLoginHSProduct } from "../config/product";
import PushToggle from "./PushToggle";

// ──────────────────────────────────────────────
// MobileTabBar — loginHS 모바일 하단 탭바 (2026-10-02 개편)
//
// 모바일에서 하는 일 = 밖에서 세번 찾아보기 · 메모 보기/적기(사진 첨부) · 변경고시·공지 확인 · 짧은 도구.
//   하단 5칸: 홈(검색) · 관세율표 · mymemo · 새소식 · 더보기
//   더보기 시트: 통관정보 · 관세제도 · 도구(환율·세금 계산기…) · 품목분류사례 · 문의 · 사용량 · 앱 설치 · 계정
//   모바일에서 뺀 것(PC 전용): 대량 세번분류 · 마스터 세번관리 · 결정트리 검토 · HS 비교 · 동의어 · 관리자 —
//     넓은 표·대량 입력이 본업이라 휴대폰 화면에서 쓸 수 없다. 주소로 들어오면 화면은 그대로 열린다.
// 메뉴 권한(관리자가 끈 메뉴)은 그대로 따른다. 데스크톱/DocuAI 에서는 렌더되지 않는다(App.js isMobileShell).
// ──────────────────────────────────────────────

/** 하단 칸 — id 는 menuConfig 의 메뉴 id(권한 확인용), 홈은 메뉴가 아니라 늘 보인다. */
export const MOBILE_TABS = [
  { id: "hs-home", label: "홈", icon: Home, href: "/" },
  { id: "hs-ai-classify", label: "관세율표", icon: BookOpen },
  { id: "hs-my-reports", label: "mymemo", icon: StickyNote },
  { id: "hs-news", label: "새소식", icon: Newspaper },
];

/** 더보기 시트 — 순서대로. alwaysOnMobile 은 데스크톱에서 상위 메뉴 아래 숨겨 둔 것(기본 숨김)이라도 모바일에선 꺼내 보인다. */
export const MOBILE_MORE = [
  { id: "hs-req-guide", label: "통관정보", icon: Search },
  { id: "hs-customs-guide", label: "관세제도", icon: Scale },
  { id: "hs-tools", label: "도구 · 환율 · 세금 계산기", icon: Calculator },
  { id: "hs-precedents-explorer", label: "품목분류사례", icon: FileText, alwaysOnMobile: true },
  { id: "inquiry-board", label: "문의·요청", icon: MessageSquare },
  { id: "hs-usage", label: "사용량", icon: Gauge },
];

/** PC 전용 — 모바일 메뉴에 넣지 않는다. */
export const DESKTOP_ONLY_IDS = ["hs-bulk-classify", "hs-master", "hs-ontology-review", "hs-compare", "hs-synonyms-admin"];

function isStandalone() {
  try {
    return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
  } catch {
    return false;
  }
}

const MobileTabBar = ({
  activeTab,
  setActiveTab,
  menuVisibility,
  isManager,
  userGroup,
  userId,
  onLogout,
  onOpenAccountSettings,
}) => {
  const [moreOpen, setMoreOpen] = useState(false);

  let visible = resolveVisibleMenus(MENU_ITEMS, menuVisibility, isManager);
  if (isLoginHSProduct()) visible = visible.filter((m) => m.category === "hs");
  const byId = Object.fromEntries(MENU_ITEMS.map((m) => [m.id, m]));
  const visibleIds = new Set(visible.map((m) => m.id));
  // 관리자가 명시적으로 끈 메뉴는 모바일에서도 끈다. 기본 숨김(데스크톱에서 다른 메뉴 아래 둔 것)은 alwaysOnMobile 이면 보인다.
  const explicitlyOff = (id) => !isManager && menuVisibility
    && Object.prototype.hasOwnProperty.call(menuVisibility, id) && menuVisibility[id] !== true;
  const allowed = (t) => t.id === "hs-home" || visibleIds.has(t.id) || (t.alwaysOnMobile && byId[t.id] && !explicitlyOff(t.id));

  const tabs = MOBILE_TABS.filter(allowed).map((t) => ({ ...t, href: t.href || byId[t.id]?.href, spa: true }));
  const more = MOBILE_MORE.filter(allowed).map((t) => ({ ...t, href: byId[t.id]?.href, spa: byId[t.id]?.spa !== false }));
  const moreActive = more.some((m) => m.id === activeTab);

  const go = (item) => {
    setMoreOpen(false);
    if (item.href) {
      if (item.spa) {
        window.history.pushState({}, "", item.href);
        window.dispatchEvent(new PopStateEvent("popstate"));
      } else {
        window.location.href = item.href;
      }
    }
    else if (item.id) setActiveTab(item.id); // 주소가 있는 메뉴는 App 의 주소 → 탭 연결이 맞춘다
  };

  return (
    <>
      {moreOpen && (
        <>
          <div onClick={() => setMoreOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 58, background: "rgba(15,17,26,.28)" }} />
          <div role="dialog" aria-label="더보기" data-testid="mobile-more" style={sheetStyle}>
            <div style={{ display: "flex", alignItems: "center", padding: "12px 16px 8px", gap: 10 }}>
              <CircleUser size={28} strokeWidth={1.4} color="#6b7280" />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700 }}>{userGroup}</div>
                <div style={{ fontSize: 12, color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis" }}>{userId}</div>
              </div>
              <button type="button" onClick={() => setMoreOpen(false)} aria-label="닫기" style={iconBtn}><X size={18} /></button>
            </div>
            <div style={{ borderTop: "1px solid var(--audit-border, #e5e7eb)" }}>
              {more.map((m) => {
                const Icon = m.icon;
                return (
                  <button key={m.id} type="button" onClick={() => go(m)} style={{ ...rowStyle, color: activeTab === m.id ? "var(--audit-accent, #1a73e8)" : "#1f2937" }}>
                    <Icon size={18} strokeWidth={1.6} /> {m.label}
                  </button>
                );
              })}
            </div>
            <div style={{ borderTop: "1px solid var(--audit-border, #e5e7eb)" }}>
              {!isStandalone() && (
                <button type="button" onClick={() => go({ href: "/?page=install", spa: false })} style={rowStyle} data-testid="mobile-install">
                  <Download size={18} strokeWidth={1.6} /> 앱으로 설치(홈 화면에 추가)
                </button>
              )}
              <PushToggle variant="row" rowStyle={rowStyle} />
              {onOpenAccountSettings && (
                <button type="button" onClick={() => { setMoreOpen(false); onOpenAccountSettings(); }} style={rowStyle}>
                  <SettingsIcon size={18} strokeWidth={1.6} /> 내 계정 설정
                </button>
              )}
              <button type="button" onClick={onLogout} style={{ ...rowStyle, color: "#b42318" }}>
                <LogOut size={18} strokeWidth={1.6} /> 로그아웃
              </button>
            </div>
          </div>
        </>
      )}

      <nav aria-label="주 메뉴" style={navStyle}>
        {tabs.map((t) => {
          const Icon = t.icon;
          const on = activeTab === t.id && !moreOpen;
          return (
            <button key={t.id} type="button" onClick={() => go(t)} style={tabStyle(on)} aria-current={on ? "page" : undefined}>
              <Icon size={21} strokeWidth={on ? 2.2 : 1.6} />
              <span style={labelStyle(on)}>{t.label}</span>
            </button>
          );
        })}
        <button type="button" onClick={() => setMoreOpen((v) => !v)} style={tabStyle(moreOpen || moreActive)} data-testid="mobile-more-tab" aria-expanded={moreOpen}>
          <MoreHorizontal size={21} strokeWidth={moreOpen || moreActive ? 2.2 : 1.6} />
          <span style={labelStyle(moreOpen || moreActive)}>더보기</span>
        </button>
      </nav>
    </>
  );
};

const navStyle = {
  position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 57,
  display: "flex", alignItems: "stretch",
  background: "var(--audit-bg, #fff)", borderTop: "1px solid var(--audit-border, #e5e7eb)",
  paddingBottom: "env(safe-area-inset-bottom)",
};
const tabStyle = (on) => ({
  flex: 1, minWidth: 0, minHeight: 54, border: "none", background: "transparent",
  padding: "7px 2px 6px", display: "flex", flexDirection: "column", alignItems: "center", gap: 3,
  cursor: "pointer", color: on ? "var(--audit-accent, #1a73e8)" : "#6b7280",
});
const labelStyle = (on) => ({
  fontSize: 10.5, fontWeight: on ? 700 : 500, whiteSpace: "nowrap", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis",
});
const sheetStyle = {
  position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 59,
  background: "var(--audit-bg-elevated, #fff)", borderRadius: "16px 16px 0 0",
  boxShadow: "0 -8px 28px rgba(0,0,0,0.16)",
  paddingBottom: "calc(8px + env(safe-area-inset-bottom))", maxHeight: "80vh", overflowY: "auto",
};
const rowStyle = {
  width: "100%", display: "flex", alignItems: "center", gap: 12, minHeight: 48,
  padding: "0 18px", border: "none", background: "transparent", fontSize: 15, color: "#1f2937",
  cursor: "pointer", textAlign: "left", fontFamily: "inherit",
};
const iconBtn = { border: "none", background: "transparent", padding: 6, cursor: "pointer", color: "#6b7280", display: "inline-flex" };

export default MobileTabBar;
